import { Injectable } from '@angular/core';
import { AppSettings, ErrorCodes } from '../util/utils';
import { HttpClientService } from '../connections/http-client.service';
import { AnalyticsService } from '../analytics/analytics.service';
import { AlertService } from '../util/alert.service';

@Injectable({
    providedIn: 'root'
})
export class CarService {
    private auctionListFilters: any = {};

    constructor(private myhttp: HttpClientService, private analytics: AnalyticsService, private alert: AlertService) {}

    public selectFilters(filters: any) {
        Object.assign(this.auctionListFilters, filters);
    }

    public resetFilters() {
        this.auctionListFilters = {};
    }

    public getFilters(): any {
        return this.auctionListFilters;
    }

    getAuction(uid: string): Promise<any> {
        return this.myhttp.post('get_auction', { uid });
    }

    getAuctionInfo(
        page: number,
        watching: boolean = false,
        bidding: boolean = false,
        sort: string = ''
    ): Promise<any[]> {
        return this.myhttp.post('get_auctions', { ...this.auctionListFilters, watching, bidding, page, sort });
    }

    getAuctions(params: any): Promise<any[]> {
        return this.myhttp.post('get_auctions', params);
    }

    getUpcomingAuctions(params: any): Promise<any[]> {
        return this.myhttp.post('upcoming_auctions', params);
    }

    getClosedAuctions(params: any): Promise<any[]> {
        return this.myhttp.post('ended_auctions', params);
    }

    getUserCar(uid: string): Promise<any> {
        return this.myhttp.post('get_vehicle', { uid });
    }

    getUserCars(): Promise<any> {
        return this.myhttp.post('get_vehicles', {});
    }

    async getAllImages(carID: string) {
        return this.myhttp.post('get_images', { item_uid: carID });
    }

    addToWatchlist(auctionID: string) {
        return this.toglleWatchlist(auctionID, true);
    }

    removeFromWatchlist(auctionID: string) {
        return this.toglleWatchlist(auctionID, false);
    }

    private toglleWatchlist(auctionID: string, watch: boolean) {
        const action = watch ? 'watch' : 'unwatch';
        return this.myhttp.post(action, { uid: auctionID });
    }

    async getMinimumBid() {
        return this.myhttp.post('get_minimum_increment', {});
    }

    bid(proxy: boolean, amount: number, auctionID: string): Promise<any> {
        const payload = {
            auction_uid: auctionID,
            amount: +amount,
            proxy
        };
        return new Promise((resolve, reject) => {
            this.myhttp
                .post(
                    'bid',
                    payload,
                    (data, info) => {
                        return { data, info };
                    },
                    { showDefaultAlert: false, errorCode: ErrorCodes.GCSubscriptionExpired }
                )
                .then((result) => resolve(result))
                .catch((err: { message: string; code: number }) => {
                    if (err.code && err.code === ErrorCodes.GCSubscriptionExpired) {
                        this.alert.showCustomDismiss(undefined, err.message, 'OK', 'alert-sub-expired-error');
                    }
                    reject(err.message);
                });
        });
    }

    getAuctionStatus(auctionID: string) {
        return this.myhttp.post(
            'status',
            { uid: auctionID },
            (data) => {
                return data;
            },
            { showDefaultAlert: false }
        );
    }

    deleteListing(vehicleId: string, car?: any): Promise<any> {
        this.analytics.logEvent('listing_deleted', { car });
        return this.myhttp.post('delete_vehicle', { uid: vehicleId });
    }

    acceptOffer(auctionUid: any) {
        return this.myhttp.post('accept_offer', { uid: auctionUid });
    }

    declineOffer(auctionUid: any) {
        return this.myhttp.post('decline_offer', { uid: auctionUid });
    }

    getConditionReportAnswers(carUid: string) {
        return this.myhttp.post(
            'vehicle_report',
            { uid: AppSettings.conditionReportId, vehicle_uid: carUid },
            (data) => {
                if (!data.answers) {
                    return;
                }

                const answersWithQuestions = {};

                data.questions.forEach((question) => {
                    const { answer, description } = this.findAnswer(data.answers, question.uid);
                    const newObj = {
                        question: question.question,
                        answer,
                        description
                    };
                    if (!answersWithQuestions[question.section]) {
                        answersWithQuestions[question.section] = [];
                    }
                    answersWithQuestions[question.section].push(newObj);
                });

                return answersWithQuestions;
            }
        );
    }

    private findAnswer(answers: any[], questionUid: string): { answer: any; description: string } {
        let ans = 'No answer';
        let desc;

        for (const answer of answers) {
            if (answer.question_uid === questionUid) {
                ans = answer.response.toString();
                desc = answer.description;
                break;
            }
        }
        return { answer: ans, description: desc };
    }

    public getBidHistory(auctionUid: string) {
        return this.myhttp.post('history', { uid: auctionUid });
    }

    public getRecentAuctions(): Promise<any> {
        return this.myhttp.post('recent', {});
    }

    public getVehicleValueFromVin(vehicle: any): Promise<any> {
        return this.myhttp.post(
            'pricing',
            {
                vin: vehicle.vin,
                odometer: +vehicle.odometer,
                post_code: vehicle.post_code
            },
            (data) => {
                return data;
            },
            { showDefaultAlert: false }
        );
    }

    public confirmSold(info: any) {
        return this.myhttp.post('confirm', info);
    }

    public cancelAuction(auctionUid: string) {
        return this.myhttp.post('cancel_auction', { uid: auctionUid });
    }

    public getContactInfo(auctionUid: string) {
        return this.myhttp.post('contact_info', { uid: auctionUid });
    }

    public getCarPricing(car: {}): Promise<any> {
        return this.myhttp.post(
            'pricing',
            car,
            (data) => {
                return data;
            },
            { showDefaultAlert: false }
        );
    }

    public counterOffer(auctionUid: string, amount: number): Promise<any> {
        return this.myhttp.post('counter_offer', { auction_uid: auctionUid, amount });
    }

    public getVinFromImage(image: string) {
        const pictureParts = image.split('base64,');
        return this.myhttp.post('ocr_vin', { image_base64: pictureParts[1] }, (data) => data, {
            showDefaultAlert: false
        });
    }

    public buyNow(auctionUid: string) {
        return this.myhttp.post('buy_now', { auction_uid: auctionUid });
    }

    public askQuestion(auction_uid: string, question: string): Promise<any> {
        return this.myhttp.post('ask_question', { auction_uid, question });
    }

    public answerQuestion(auction_uid: string, question_uid, answer: string): Promise<any> {
        return this.myhttp.post('answer_question', { auction_uid, question_uid, answer });
    }
}

export interface Pricing {
    retail: number;
    auction: number;
    trade_in: number;
    groovy: number;
    groovy_range: any;
    groovy_high_int: number;
    groovy_low_int: number;
    min?: number;
    middle?: number;
    max?: number;
}
